"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import {
  comparableTeams,
  formatMetricValue,
  metricValue,
  styleTagsFor,
  STYLE_AXES,
  useTournamentReplayMetrics,
  useTournamentTeamStats,
} from "@/entities/replay-metrics";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { DeferredMount } from "@/shared/ui/layout/deferred-mount";

import { BehaviorBoards } from "./behavior-boards";
import { ResultsPodium, type PodiumEntry } from "./results-podium";
import { SectionHeading } from "./section-heading";
import { TeamRosterRail, type RailTeam } from "./team-roster-rail";
import { TournamentMvpSection } from "./mvp-section";
import { TournamentPlayerProfileSection } from "./player-profile-section";
import { TournamentPlayersTableSection } from "./players-table-section";
import type { TeamBreakdown } from "./team-breakdown-dialog";

/**
 * KAN-878: секции итогов турнира — одна сборка на архив и на живую вкладку.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ. Итоги жили в двух местах: `tabs/tournament-results-tab.tsx` у
 * живого турнира и `tournament-archives/tournament-archive-results.tsx` у
 * архивного. Пока секции совпадали, расхождение было незаметным; после редизайна
 * архив уехал на новые блоки, а живая вкладка осталась на старых — и вид страницы
 * стал меняться в момент архивации. Это ровно жалоба KAN-787, воспроизведённая
 * заново, только теперь беднее выглядит НЕ архив, а недоигранный турнир.
 *
 * ВЫБРАННЫЙ ИГРОК ЖИВЁТ ЗДЕСЬ. Его поднимают три секции — пьедестал, лента
 * составов и таблица игроков, — а показывает четвёртая. Оставь состояние в любой
 * из них, и остальные спорили бы за то, кто показан.
 *
 * РАЗЛИЧИЯ ПРОКИДЫВАЮТСЯ ПРОПАМИ, А НЕ ДВУМЯ КОПИЯМИ РАЗМЕТКИ. Архив читает
 * замороженный снимок, живой турнир — живые ручки; и то и другое приводится
 * страницей к одному виду до входа сюда. Секции, которым хватает `tournamentId`,
 * данные тянут сами.
 */

/**
 * Порог сравнения с бэкенда (`PercentileMath.MinGames`). Здесь он нужен только для
 * ФОРМУЛИРОВКИ причины, а не для расчёта: балл считает бэкенд, и если порог там
 * изменится, поедет текст подсказки, но не цифры на странице.
 */
const MIN_GAMES_FOR_SCORE = 3;

export function TournamentResultsSections({
  tournamentId,
  podiumEntries,
  teams,
  podiumTitle,
  rosterTitle,
}: {
  tournamentId: string;
  /** Пьедестал. Пусто — секции нет: у недоигранного турнира мест ещё не существует. */
  podiumEntries?: PodiumEntry[];
  /**
   * Составы команд, приведённые к общему виду. Не передали — ленты нет: у 1v1
   * команд не бывает по определению, и пустая карусель читалась бы как поломка.
   */
  teams?: RailTeam[];
  /**
   * Заголовок пьедестала. Не передали — берётся из словаря. Проп остаётся, потому что
   * у живого турнира заголовок другой по смыслу: там это ещё не итог, а текущее
   * положение. Передавать сюда готовую строку из вызова допустимо только при условии,
   * что она сама пришла из словаря: русский текст в пропе — это ровно то, из-за чего
   * страница оставалась русской на английской локали.
   */
  podiumTitle?: string;
  /** Заголовок ленты составов. Не передали — из словаря. */
  rosterTitle?: string;
}) {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const tStats = useTranslations("TournamentStats");
  const tSection = useTranslations("TournamentStats.sections");

  // Балл для колец в пьедестале. Тот же источник, что у таблицы и профиля
  // (GL Score, KAN-764): второй источник развёл бы одну оценку между секциями.
  const { data: metrics } = useTournamentReplayMetrics(tournamentId);
  const scoreByUserId = useMemo(() => {
    const map: Record<string, number | null> = {};
    for (const player of metrics ?? []) {
      if (player.userId && typeof player.glScore === "number") {
        map[player.userId] = player.glScore;
      }
    }
    return map;
  }, [metrics]);

  /**
   * ПОЧЕМУ балла нет — по игрокам.
   *
   * На проде это выглядело поломкой: у одних в составе кольцо с числом, у других
   * пусто, и ничто не объясняло разницу. Причин ровно две, и они разные по смыслу:
   *
   *  - игрок В метриках есть, а балла нет — разобранных игр меньше порога (три) либо
   *    в роли не набралось тех, с кем сравнивать;
   *  - игрока в метриках НЕТ ВОВСЕ — его игры не попали в разбор. На проде это 4 из
   *    42 участников: их строки реплеев не привязаны к аккаунтам (в покрытии турнира
   *    это те самые «непривязанные записи»), поэтому человек существует в зачёте
   *    турнира и отсутствует в метриках.
   *
   * Пустое место не различает эти случаи и читается как «у игрока балла не бывает».
   * Ноль подставлять нельзя: «мало игр» и «сыграл на ноль» — разные утверждения.
   */
  const scoreNoteByUserId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const player of metrics ?? []) {
      if (!player.userId || typeof player.glScore === "number") continue;
      const games = player.gamesTotal ?? 0;
      map[player.userId] =
        games > 0 && games < MIN_GAMES_FOR_SCORE
          ? tStats("scoreNote.fewGames", { games })
          : tStats("scoreNote.noPeers");
    }
    return map;
  }, [metrics, tStats]);

  // Разбор команд для модалки в ленте. Считается ЗДЕСЬ, где видны все команды:
  // ярлык стиля — это сравнение с медианой по сопоставимым командам турнира, и
  // внутри модалки одной команды сравнивать было бы не с чем (KAN-796).
  const { data: teamMetrics } = useTournamentTeamStats(tournamentId);
  const breakdownByTeamName = useMemo(() => {
    const rows = teamMetrics ?? [];
    if (rows.length === 0) return undefined;

    const comparable = comparableTeams(rows);
    const map: Record<string, TeamBreakdown> = {};

    // КЛЮЧ — `teamId` МЕТРИК, КОТОРЫЙ СШИВАЕТСЯ С `originalTeamId` ЛЕНТЫ, А НЕ С `id`.
    //
    // Это стоило половины дня, поэтому пояснение здесь. У архивной записи команды
    // два идентификатора: `id` — идентификатор самой архивной записи, и
    // `originalTeamId` — идентификатор команды турнира. Метрики реплеев приходят с
    // третьим, `draft_teams.Id`, и внешнего ключа между таблицами нет вовсе; бэкенд
    // восстанавливает связь по паре «турнир + капитан» (KAN-909) и с KAN-909 отдаёт
    // уже `teams.Id`, то есть то же, что `originalTeamId`.
    //
    // Соблазн взять `id` выглядит естественным — он первый в объекте и называется
    // короче. Он не совпадёт ни с чем, и разбор молча пропадёт у всех команд: обе
    // стороны строки, подстановка типобезопасна, ошибку показывает только стенд.
    //
    // До KAN-909 здесь стоял обход по нормализованному названию. Он работал, но при
    // командах-тёзках не показывал разбор ни одной из них — цена, которой больше нет.
    for (const row of rows) {
      const key = row.teamId;
      if (!key) continue;
      map[key] = {
        teamName: row.teamName ?? "",
        tags: styleTagsFor(row, comparable),
        metrics: STYLE_AXES.flatMap((axis) => {
          const value = metricValue(row, axis);
          return value === null ? [] : [{ key: axis, value: formatMetricValue(axis, value) }];
        }),
        pairs: row.championPairs,
      };
    }

    return map;
  }, [teamMetrics]);

  const withRoster = (teams ?? []).filter((t) => (t.roster ?? []).length > 0);

  return (
    <>
      {podiumEntries && podiumEntries.length > 0 ? (
        <Card>
          <CardContent className="pt-6">
            <SectionHeading title={podiumTitle ?? tSection("podiumTitle")} />
            <ResultsPodium
              entries={podiumEntries}
              scoreByUserId={scoreByUserId}
              scoreNoteByUserId={scoreNoteByUserId}
              onSelectPlayer={setSelectedPlayerId}
            />
          </CardContent>
        </Card>
      ) : null}

      <TournamentMvpSection tournamentId={tournamentId} />

      {/* БЕЗ ОБЁРТКИ-КАРТОЧКИ, и это требование макета, а не вкус: карточки команд
          в стопке уже карточки, и рамка вокруг рамки читается как вложенный блок.
          Подпись даёт лёгкий секционный заголовок. */}
      {withRoster.length > 0 ? (
        <section>
          <SectionHeading title={rosterTitle ?? tSection("rosterTitle")} />
          <TeamRosterRail
            teams={withRoster}
            onSelectPlayer={setSelectedPlayerId}
            breakdownByTeamId={breakdownByTeamName}
          />
        </section>
      ) : null}

      {/* Ниже первого экрана — монтаж на подходе (KAN-1243): таблица, топы и профиль
          вместе давали ~1,7 тыс. узлов в первой задаче гидрации. Выбранный игрок
          монтирует профиль сразу — к нему ведёт клик в пьедестале или составе. */}
      <DeferredMount minHeight={640}>
        <TournamentPlayersTableSection
          tournamentId={tournamentId}
          scoreNoteByUserId={scoreNoteByUserId}
          selectedUserId={selectedPlayerId}
          onSelectPlayer={setSelectedPlayerId}
        />
      </DeferredMount>

      <DeferredMount minHeight={320}>
        <BehaviorBoards tournamentId={tournamentId} />
      </DeferredMount>

      <DeferredMount minHeight={480} force={Boolean(selectedPlayerId)}>
        <TournamentPlayerProfileSection
          tournamentId={tournamentId}
          selectedUserId={selectedPlayerId}
          onSelectPlayer={setSelectedPlayerId}
        />
      </DeferredMount>
    </>
  );
}
